// eslint-disable-next-line import/no-unresolved
import { navigate } from '@reach/router';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock-upgrade';
import PropTypes from 'prop-types';
import React, {
  useEffect, useRef, useState,
} from 'react';
import SimpleBar from 'simplebar-react';
import { v4 as uuidv4 } from 'uuid';
import 'simplebar-react/dist/simplebar.min.css';

import { Icon } from '@powdr/components';
import { useNavigationHistory } from '@powdr/hooks';
import { isBuildTime } from '@powdr/utils';

import {
  CloseButton,
  CloseButtonAlt,
  ModalOverlay,
  DialogAlert,
  DialogPage,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  ContentSpacer,
} from './styles';

export const Modal = ({
  closeModalLink = null,
  header = null,
  footer = null,
  handleCloseModal = null,
  isDialogAlert = false,
  isFullScreen = false,
  removeScroller = false,
  colorProfile = null,
  isMiniModal = false,
  trackingTitle = null,
  children,
}) => {
  const [modalUUID, setModalUUID] = useState(null);
  const [hasNestedModal, setHasNestedModal] = useState(false);
  const modalOverlayRef = useRef();
  const checkIfModalHasNestedModal = () => ((modalOverlayRef?.current) ? modalOverlayRef?.current?.querySelector('.modal-overlay') !== null : false);
  const { isDirectLink } = useNavigationHistory();

  useEffect(() => {
    if (!isBuildTime()) {
      disableBodyScroll(modalOverlayRef, {
        allowTouchMove: (el) => {
          while (el && el !== document?.body) {
            if (el.classList.contains('body-scroll-lock-ignore')) {
              return true;
            }

            el = el.parentElement;
          }
        },
      });
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [modalOverlayRef]);

  useEffect(() => {
    setHasNestedModal(checkIfModalHasNestedModal());
  }, [modalOverlayRef]);

  const handleCloseButtonClick = () => {
    if (handleCloseModal) {
      handleCloseModal();
    } else if (isDirectLink && closeModalLink) {
      navigate(closeModalLink);
    } else {
      window.history.back();
    }
  };

  const content = (
    <>
      { header && <DialogHeader>{header}</DialogHeader>}
      <DialogBody>{children}</DialogBody>
      { footer && <DialogFooter>{footer}</DialogFooter>}
    </>
  );

  useEffect(() => {
    setModalUUID(uuidv4());
  }, []);

  return (
    <ModalOverlay
      ref={modalOverlayRef}
      id={`modal-${modalUUID}`}
      title={trackingTitle}
      className="modal-overlay"
      $colorProfile={colorProfile}
      $modalID={`modal-${modalUUID}`}
      hasNestedModal={hasNestedModal}
      $isMiniModal={isMiniModal}
    >
      { isDialogAlert
        ? (
          <DialogContent
            className="body-scroll-lock-ignore"
            isDialogAlert={isDialogAlert}
            $colorProfile={colorProfile}
            body-scroll-lock-ignore="true"
          >
            <DialogAlert $colorProfile={colorProfile}>
              {children}
            </DialogAlert>
          </DialogContent>
        )
        : (
          <DialogPage
            className="dialog-page body-scroll-lock-ignore"
            $isFullScreen={isFullScreen}
            $colorProfile={colorProfile}
            $isMiniModal={isMiniModal}
            body-scroll-lock-ignore="true"
          >
            <ContentSpacer $isMiniModal={isMiniModal}>
              {isFullScreen ? (
                <CloseButtonAlt
                  title={trackingTitle}
                  aria-label="close"
                  onClick={handleCloseButtonClick}
                  type="button"
                  $colorProfile={colorProfile}
                >
                  <span className="close">Close</span>
                  <span className="button"><Icon name="ui-close-circle" width="36" height="36" /></span>
                </CloseButtonAlt>
              )
                : (
                  <CloseButton
                    title={trackingTitle}
                    aria-label="close"
                    onClick={handleCloseButtonClick}
                    type="button"
                    $colorProfile={colorProfile}
                  >
                    <Icon name="ui-close" width="15" height="15" />
                  </CloseButton>
                )}
            </ContentSpacer>
            <DialogContent
              className="dialog-content body-scroll-lock-ignore"
              $colorProfile={colorProfile}
              $isMiniModal={isMiniModal}
              body-scroll-lock-ignore="true"
            >
              {(removeScroller) ? content : <SimpleBar style={{ height: '100%' }}>{content}</SimpleBar>}
            </DialogContent>
          </DialogPage>
        )}
    </ModalOverlay>
  );
};

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  closeModalLink: PropTypes.string,
  header: PropTypes.node,
  footer: PropTypes.node,
  handleCloseModal: PropTypes.func,
  isDialogAlert: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  colorProfile: PropTypes.string,
  removeScroller: PropTypes.bool,
  isMiniModal: PropTypes.bool,
  trackingTitle: PropTypes.string,
};
